import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { CashoutErrorType } from '@bpos/v1/cashout';
import { EarlyPayoutConfig, MyBetsVisualizationTrackingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { EarlyPayoutErrors } from '@frontend/sports/types/components/sitecore';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { NativeAppService } from '@frontend/vanilla/core';

import { EarlyPayoutUtils } from '../../../my-bets-base/early-payout/early-payout-utils';
import { EarlyPayoutHelpers } from '../../../my-bets-base/early-payout/my-bets-earlypayout-helpers';
import {
    EarlyPayoutErrorTypes,
    EarlyPayoutOptionErrors,
    EarlyPayoutStates,
    PayoutError,
    PayoutPushError,
} from '../../../my-bets-base/models/early-payout-types';
import { MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { EarlypayoutButtonState, MyBetsEarlypayoutButtonStateService } from '../../services/my-bets-earlypayout-button-state.service';
import { MyBetsUtilService } from '../../services/my-bets-util.service';
import { MyBetsVisualizationTrackingService } from '../../services/my-bets-visualization-tracking.service';

@Component({
    selector: 'ms-my-bets-earlypayout-container',
    templateUrl: 'my-bets-earlypayout-container.html',
    styleUrl: 'my-bets-earlypayout-container.scss',
    standalone: false,
})
export class MyBetsEarlyPayoutContainerComponent implements OnInit {
    @Input() betslip: MyBetsBetSlip;
    @Input() myBetsListContext: MyBetsListContext;
    @Input() shouldRefreshPosition = false;
    @Output() autoCashoutPopperTriggered = new EventEmitter<'open' | 'close'>();
    betCardRedesigned = this.myBetsUtilService.isBetCardRedesigned();
    earlyPayoutErrors = EarlyPayoutErrorTypes;

    private readonly earlyPayoutErrorsforHidingSettings = new Set([
        CashoutErrorType.AutoCashoutNotAllowed.toLowerCase(),
        CashoutErrorType.CashoutNotAllowed.toLowerCase(),
    ]);

    betTypeInfo: string;

    constructor(
        public epConfig: EarlyPayoutConfig,
        private nativeAppService: NativeAppService,
        private m2User: UserService,
        public sitecore: Sitecore,
        private earlyPayoutUtils: EarlyPayoutUtils,
        private changeRef: ChangeDetectorRef,
        private eyBetsEarlypayoutButtonStateService: MyBetsEarlypayoutButtonStateService,
        private earlyPayoutHelpers: EarlyPayoutHelpers,
        private myBetsVisualizationTrackingService: MyBetsVisualizationTrackingService,
        private mybetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
        private myBetsUtilService: MyBetsUtilService,
        private userSettingsService: SyncUserSettingsService,
    ) {}

    ngOnInit(): void {
        this.betTypeInfo = this.mybetsVisualizationTrackingConfig.isEnabled
            ? this.myBetsVisualizationTrackingService.getBetTypeInfo(this.betslip)
            : (this.betslip.sgpType ?? this.betslip.betType);
    }

    get showRedesignDivider(): boolean {
        return (
            this.betCardRedesigned &&
            ((this.earlyPayoutUtils.isEarlyPayoutAvailable(this.betslip) && !this.earlyPayoutHelpers.earlyPayoutIsNotOffered(this.betslip)) ||
                this.myBetsListContext.listType !== BetslipListType.Settled)
        );
    }

    get showCashoutSettingsButton(): boolean {
        const error = this.payoutError;

        if (error && this.earlyPayoutHelpers.isLiveSgpError(error.type, error.reason)) {
            return false;
        }

        const hasCashoutNotification =
            this.nativeAppService.isNative && this.betslip.earlyPayout && this.betslip.earlyPayout.autoCashoutNotificationValue;

        const hasCashoutSet = this.betslip.earlyPayout && this.betslip.earlyPayout.autoCashOutValue;

        if (hasCashoutSet) {
            return this.eyBetsEarlypayoutButtonStateService.buttonState(this.betslip) !== EarlypayoutButtonState.PaidOut;
        }

        if (hasCashoutNotification) {
            return true;
        }

        if (!this.epConfig.isAutoCashOutEnabled || this.userSettingsService.bettingSettings.earlyPayoutDeactivated) {
            return false;
        }

        if (this.isNotAllowedSgpCompetition()) {
            return false;
        }

        return this.isEarlyPayoutStateAllowingSettings();
    }

    private get isCashoutAvailable(): boolean {
        return this.earlyPayoutUtils.isEarlyPayoutAvailable(this.betslip);
    }

    private isEarlyPayoutStateAllowingSettings(): boolean {
        if (!this.betslip || !this.betslip.earlyPayout || !this.betslip.earlyPayout.state) {
            return false;
        }

        return Boolean(
            this.betslip.earlyPayout.state === EarlyPayoutStates.Allowed ||
                this.betslip.earlyPayout.state === EarlyPayoutStates.Confirm ||
                this.betslip.earlyPayout.state === EarlyPayoutStates.Disabled,
        );
    }

    get payoutError(): PayoutError | undefined {
        if (!this.betslip || !this.betslip.earlyPayout) {
            return;
        }

        return this.betslip.earlyPayout.error;
    }

    get hideCashoutButton(): boolean {
        if (this.earlyPayoutUtils.validateAdditionalPrechecks(this.betslip)) {
            return true;
        }

        if (!this.betslip || !this.betslip.earlyPayout || this.betCardRedesigned) {
            return false;
        }
        const errorType = this.earlyPayoutHelpers.getEarlyPayoutErrorTypeLowerCase(
            this.betslip.earlyPayout.error?.type,
            this.betslip.earlyPayout.error?.reason,
        );

        return (
            errorType === EarlyPayoutOptionErrors.OptionMarketTemplateNotAllowed.toLowerCase() ||
            errorType === EarlyPayoutErrorTypes.BetNotAllowedForCashout.toLowerCase()
        );
    }

    get payoutErrorMessage(): string | undefined {
        return this.sitecore.earlyPayoutErrors[this.payoutError!.type as keyof EarlyPayoutErrors];
    }

    get payoutPushError(): PayoutPushError | undefined {
        if (this.payoutError) {
            return this.mapErrorType(this.payoutError.type, this.payoutError.reason);
        }

        if (this.betslip.earlyPayout && this.m2User.columnSize > this.betslip.earlyPayout.value) {
            // Set specific message for greek labels
            return this.mapErrorType(EarlyPayoutErrorTypes.CashoutNotFound);
        }

        return undefined;
    }

    get showPayoutPushErrorHidden(): boolean {
        return (
            this.payoutPushError?.name.toLowerCase() === EarlyPayoutOptionErrors.OptionHiddenSingle.toLowerCase() ||
            this.payoutPushError?.name.toLowerCase() === EarlyPayoutOptionErrors.OptionHiddenCombo.toLowerCase()
        );
    }

    get isEarlyPayoutError(): boolean {
        return !!this.payoutPushError && this.earlyPayoutErrorsforHidingSettings.has(this.payoutPushError.name.toLowerCase());
    }

    get showEarlyPayoutSettingButton(): boolean {
        const earlyPayoutError = this.betslip.earlyPayout?.error;
        const showButton = !this.isEarlyPayoutError && this.isCashoutAvailable && this.showCashoutSettingsButton;
        if (!this.betCardRedesigned) {
            return showButton;
        }
        return (
            showButton &&
            (!this.earlyPayoutHelpers.earlyPayoutIsNotOffered(this.betslip) ||
                this.earlyPayoutHelpers.isLiveSgpError(earlyPayoutError?.type, earlyPayoutError?.reason))
        );
    }

    onStateChanged() {
        this.changeRef.detectChanges();
    }

    autoCashoutTriggered($event: any): void {
        this.autoCashoutPopperTriggered.next($event);
    }

    private mapErrorType(type: string, reason?: string): PayoutPushError | undefined {
        const errorType = this.earlyPayoutHelpers.getEarlyPayoutErrorTypeLowerCase(type, reason);
        const pushError = this.sitecore.earlyPayoutPushErrors.find((error) => error.name.toLowerCase() === errorType);

        if (pushError) {
            pushError.error.Message = interpolateDoubleBracesString(pushError.error.Message, { currency: this.betslip.stake.currency.toUpperCase() });
        }

        return pushError;
    }

    private isNotAllowedSgpCompetition(): boolean | undefined {
        return (
            this.betslip.sgpType &&
            this.betslip.bets.some((b) => b.betBuilderInfo && !this.epConfig.autoCashoutSgpAllowedCompetitions[b.sportId]?.includes(b.leagueId))
        );
    }
}
